import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Fade, Switch, Typography } from "@mui/material";
import SchoolSwitcherTrigger, {
  SchoolSwitcherPanel,
  useStaffSchools,
} from "./SchoolSwitcherList";
import SchoolNavMenu, { NAV_ICON_COLOR } from "./SchoolNavMenu";
import RosterActions from "./RosterActions";
import { NavDrawerContext } from "../shared/NavDrawerButton";
import { useFixedSidebar } from "../../contexts/FixedSidebarContext";
import { NAV_SURFACE_BG, NAV_SURFACE_DIVIDER } from "./navSurface";

// The School app menu, shared by the desktop hamburger drawer (sidebar toggle
// off) and the permanent left sidebar (toggle on). It owns its own flex/padding
// so it drops into either shell unchanged.
//
// Three bands, top to bottom: a fixed header (school switcher, roster actions),
// the scrolling menu, and the pinned fixed-sidebar switch. Only the middle band
// scrolls, so the header controls stay reachable however long the menu gets.
export default function SchoolNavContents({
  appProps,
  schoolSlug,
  schoolYearId = null,
  navigationMode,
}) {
  const { enabled, available, setEnabled } = useFixedSidebar();
  const { open: drawerOpen } = useContext(NavDrawerContext);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const schools = useStaffSchools(appProps?.staff?.id);
  // A staff member with a single school has nothing to switch to.
  const canSwitchSchool = schools.length > 1;
  // The open switcher spans two disjoint regions - the trigger in the fixed
  // header and the panel in the scroll body - so the outside-click check needs
  // both, not one enclosing wrapper.
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  // Collapse the switcher back to the menu whenever the surrounding drawer closes,
  // so reopening it always lands on the menu. Outside a drawer (the permanent
  // sidebar) the context defaults to open, so this never fires there.
  useEffect(() => {
    if (!drawerOpen) setSwitcherOpen(false);
  }, [drawerOpen]);

  // Close the switcher on any press outside it. The trigger is excluded so its
  // own click still reads as a toggle - closing here first would let the click
  // reopen what the press just closed. Listening on pointerdown (capture) beats
  // click, so a press that lands on a row which unmounts on mousedown still
  // counts as inside.
  useEffect(() => {
    if (!switcherOpen) return undefined;
    const handlePointerDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setSwitcherOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [switcherOpen]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: NAV_SURFACE_BG,
      }}
    >
      {/* Fixed header. Its sections carry their own padding so the dividers
          between them run the full width of the surface. */}
      <Box sx={{ flexShrink: 0 }}>
        {canSwitchSchool && (
          <Box ref={triggerRef}>
            <SchoolSwitcherTrigger
              currentSchoolName={appProps?.school?.name}
              open={switcherOpen}
              onToggle={() => setSwitcherOpen((o) => !o)}
            />
          </Box>
        )}

        {/* OneRoster import/export + DataRollup sync (gated via appProps).
            Leaves with the menu while the switcher is open, so the open
            switcher owns the whole surface. */}
        <Fade
          in={!switcherOpen}
          unmountOnExit
          timeout={{ enter: 150, exit: 0 }}
        >
          <Box>
            <RosterActions appProps={appProps} schoolSlug={schoolSlug} />
          </Box>
        </Fade>
      </Box>

      {/* Scrollable region: the menu, or the school list while the switcher is
          open. Both sides fade in and leave instantly (exit: 0), so whichever is
          going away is gone before the other arrives - nothing renders below a
          still-present sibling and jumps up when it unmounts. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
        <Fade
          in={!switcherOpen}
          unmountOnExit
          timeout={{ enter: 150, exit: 0 }}
        >
          <Box>
            <SchoolNavMenu
              appProps={appProps}
              schoolSlug={schoolSlug}
              schoolYearId={schoolYearId}
              navigationMode={navigationMode}
              onDark
            />
          </Box>
        </Fade>

        <Fade
          in={switcherOpen}
          mountOnEnter
          unmountOnExit
          timeout={{ enter: 150, exit: 0 }}
        >
          <Box ref={panelRef}>
            <SchoolSwitcherPanel
              schools={schools}
              currentSchoolSlug={schoolSlug}
            />
          </Box>
        </Fade>
      </Box>

      {/* Fixed-sidebar toggle, pinned below the scroll region. Left out
          entirely on a screen too narrow to give a page its full width beside
          the sidebar - there, the mode has nowhere to go. */}
      {available && (
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1.5,
            borderTop: NAV_SURFACE_DIVIDER,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: 14, color: NAV_ICON_COLOR }}>
            Locked to left screen
          </Typography>
          <Switch
            size="small"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            slotProps={{ input: { "aria-label": "Locked to left screen" } }}
            sx={{
              "& .MuiSwitch-track": {
                backgroundColor: "#FFFFFF",
                opacity: 0.3,
              },
              "& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb": {
                color: "#FFFFFF",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
